import { authApi } from '@/api/authApi'
import { updateCurrentUserName, updateCurrentUserSettings, usersApi } from '@/api/usersApi'
import { AUTH_STORAGE_KEY, CUSTOM_VIEWS_STORAGE_KEY_PREFIX } from '@/config/storageKeys'
import { queryClient } from '@/lib/queryClient'
import { authKeys } from '@/lib/queryKeys'
import type { Permissions } from '@/rbac/grants'
import { isPermissionGrantActive } from '@/rbac/isPermissionGrantActive'
import { forgetStreamsCacheScope } from '@/stores/streamsStore'
import { isDemoDeployment } from '@/utils/deployment'
import { isOwner } from '@/utils/permissions'
import { create } from 'zustand'
import { devtools, persist, subscribeWithSelector } from 'zustand/middleware'
import { useShallow } from 'zustand/react/shallow'
import {
	type ApiError,
	type AuthStore,
	type ConsoleUser,
	type LoginCredentials,
	type PlatformUser,
	isConsoleUser,
	isPlatformUser,
	mergeDisplayPreferences,
	redirectToLoginIf401,
} from './auth'

interface AuthState {
	user: ConsoleUser | PlatformUser | null
	userType: 'console' | 'platform' | null
	currentTenant: string
	recentTenants: string[]
	isAuthenticated: boolean
	isLoading: boolean
	error: string | null
	lastUsedProvider: string | null // Added this to store the last used OAuth provider
	currentOrganizationId: string | null // Current organization context for billing/quota
}

const MAX_RECENT_TENANTS = 3

// Serializes updateUserSettingsWith calls so each one reads settings only after all earlier
// writes have applied, not off a stale snapshot — prevents two near-simultaneous writes from
// overwriting each other (PATCH /v3/me replaces the whole settings JSON).
let settingsWriteQueue: Promise<void> = Promise.resolve()

const initialState: AuthState = {
	user: null,
	userType: null,
	currentTenant: '', // Empty string when no user is logged in
	recentTenants: [],
	isLoading: false,
	isAuthenticated: false,
	error: null,
	lastUsedProvider: null, // set last used provider
	currentOrganizationId: null,
}

export const useAuthStore = create<AuthStore>()(
	devtools(
		persist(
			subscribeWithSelector<AuthStore>((set, get) => ({
				// Initial state
				...initialState,

				// Authentication actions
				login: async (credentials: LoginCredentials) => {
					set({ isLoading: true, error: null, lastUsedProvider: credentials.provider })

					try {
						// Use provided redirect URI or default to '/' (indexRoute will handle appropriate redirect)
						const redirectUri = credentials.redirectUri ?? '/'

						// Redirect to initiate-login endpoint
						authApi.initiateLogin(credentials.provider, redirectUri)
					} catch (error: unknown) {
						const apiError = error as ApiError
						set({
							isLoading: false,
							error: apiError.message || 'Login failed',
						})
						throw error
					}
				},

				logout: async () => {
					try {
						// Clear local state before redirecting
						set({
							user: null,
							userType: null,
							currentTenant: '',
							recentTenants: [],
							isLoading: false,
							isAuthenticated: false,
							error: null,
							lastUsedProvider: null,
							currentOrganizationId: null,
						})
						// The next user may land on the same project with different permissions.
						forgetStreamsCacheScope()
						await authApi.logout()
					} catch (error) {
						console.error('Failed to logout cleanly:', error)
					}
				},

				refreshUser: async () => {
					set({ isLoading: true, error: null })

					try {
						// Scope /me to the active tenant explicitly so the returned role
						// matches the current project rather than whatever tenant is in the URL
						const user = await usersApi.getCurrentUser(get().currentTenant || undefined)

						const userType: 'console' | 'platform' = isConsoleUser(user) ? 'console' : 'platform'
						const currentOrgId: string | null = get().currentOrganizationId || user.organizations?.[0]?.id || null

						// This refetch (fired on every tenant switch) can race ahead of an in-flight
						// debounced display-preference write; merge instead of replacing so it isn't
						// silently dropped.
						const previousUser = get().user
						const mergedUser =
							isPlatformUser(user) && isPlatformUser(previousUser)
								? {
										...user,
										settings: {
											...user.settings,
											displayPreferences: mergeDisplayPreferences(
												previousUser.settings?.displayPreferences,
												user.settings?.displayPreferences,
											),
										},
									}
								: user

						set({
							user: mergedUser,
							userType,
							currentOrganizationId: currentOrgId,
							isLoading: false,
							isAuthenticated: true,
							error: null,
						})

						// Keep React Query cache for currentUser in sync with the latest /me response
						queryClient.setQueriesData({ queryKey: authKeys.currentUser() }, () => mergedUser)

						// Set current tenant if not already set (e.g., on initial login).
						// Don't overwrite if already set — switchTenant sets it explicitly.
						if (isPlatformUser(user) && user.tenant_id && !get().currentTenant) {
							set({ currentTenant: user.tenant_id })
						}

						return { user: mergedUser }
					} catch (error: unknown) {
						if (redirectToLoginIf401(error, get().clearAuthState)) {
							throw error
						}
						const apiError = error as ApiError
						// Network blips and 5xx are transient — dropping the user/tenant/org here blanks
						// the switcher mid-session. Only an access error means the session really changed.
						const lostAccess = apiError?.status >= 400 && apiError?.status < 500
						set({
							isLoading: false,
							error: apiError.message || 'Failed to refresh user',
							// Same fields clearAuthState drops, minus `error` — the message is what this branch
							// exists to keep. isAuthenticated must go with the user: authenticatedRouteGuard reads
							// only that flag, so leaving it set admits the session with no user behind it.
							...(lostAccess && {
								user: null,
								userType: null,
								currentTenant: '',
								recentTenants: [],
								currentOrganizationId: null,
								isAuthenticated: false,
							}),
						})
						throw error
					}
				},

				// User management actions
				setUser: (user: ConsoleUser | PlatformUser | null) => {
					const currentState = get()
					if (currentState.isAuthenticated && !user) {
						return
					}

					const userType: 'console' | 'platform' = isConsoleUser(user) ? 'console' : 'platform'

					// A /me refetch (e.g. on tenant switch) can race ahead of an in-flight debounced
					// display-preference write; merge instead of replacing so it isn't silently dropped.
					const mergedUser =
						isPlatformUser(user) && isPlatformUser(currentState.user)
							? {
									...user,
									settings: {
										...user.settings,
										displayPreferences: mergeDisplayPreferences(
											currentState.user.settings?.displayPreferences,
											user.settings?.displayPreferences,
										),
									},
								}
							: user

					set({
						user: mergedUser,
						userType,
						isAuthenticated: !!user,
						// Preserve currentTenant if already set (e.g., after switchTenant).
						// Only fall back to user.tenant_id on initial login when currentTenant is empty.
						currentTenant: currentState.currentTenant || (isPlatformUser(user) ? user.tenant_id || '' : ''),
						// Preserve recentTenants — don't reset on every setUser call
						recentTenants:
							currentState.recentTenants.length > 0
								? currentState.recentTenants
								: isPlatformUser(user) && user.tenant_id
									? [user.tenant_id]
									: [],
						// Preserve currentOrganizationId if already set
						currentOrganizationId: currentState.currentOrganizationId || user?.organizations?.[0]?.id || null,
					})
				},

				updateUserSettingsWith: updater => {
					const task = async () => {
						const user = get().user
						if (!user || !isPlatformUser(user)) {
							throw new Error('User settings are only available for platform users')
						}

						// PATCH /v3/me replaces the whole settings JSON, so keys explicitly set to
						// undefined are removed rather than sent through.
						const next = updater(user.settings ?? {})
						const merged: Record<string, unknown> = { ...next }
						for (const key of Object.keys(merged)) {
							if (merged[key] === undefined) {
								delete merged[key]
							}
						}
						const settings = merged as PlatformUser['settings']

						// Demo mode has no persistence — update local state only.
						if (!isDemoDeployment()) {
							await updateCurrentUserSettings(settings ?? {})
						}

						set(state => {
							if (!state.user || !isPlatformUser(state.user)) {
								return {}
							}
							return { user: { ...state.user, settings } }
						})
					}

					// Chain onto the queue regardless of the previous task's outcome, so one
					// rejected write doesn't wedge every write queued after it.
					const result = settingsWriteQueue.then(task, task)
					settingsWriteQueue = result.then(
						() => undefined,
						() => undefined,
					)
					return result
				},

				updateUserSettings: patch => get().updateUserSettingsWith(current => ({ ...current, ...patch })),

				updateUserName: async (name: string) => {
					const user = get().user
					if (!user || !isPlatformUser(user)) {
						throw new Error('User name is only available for platform users')
					}

					// Demo mode has no persistence — update local state only.
					if (!isDemoDeployment()) {
						await updateCurrentUserName(name)
					}

					set(state => {
						if (!state.user || !isPlatformUser(state.user)) {
							return {}
						}
						return { user: { ...state.user, name } }
					})
				},

				updateOrgOwners: (orgId: string, owners: string[]) => {
					set(state => {
						if (!state.user) {
							return {}
						}
						let updated = false
						let newOrganizations = state.user.organizations
						if (newOrganizations) {
							newOrganizations = newOrganizations.map(org => {
								if (org.id === orgId) {
									updated = true
									return { ...org, owners }
								}
								return org
							})
						}
						let newTenantList = isPlatformUser(state.user) ? state.user.tenant_list : undefined
						if (newTenantList) {
							newTenantList = newTenantList.map(t => {
								if (t.org_id === orgId && t.organization) {
									updated = true
									return { ...t, organization: { ...t.organization, owners } }
								}
								return t
							})
						}
						if (!updated) {
							return {}
						}
						return {
							user: {
								...state.user,
								...(newOrganizations && { organizations: newOrganizations }),
								...(newTenantList && { tenant_list: newTenantList }),
							},
						}
					})
				},

				setCurrentTenant: (tenant: string) => {
					set({ currentTenant: tenant })
				},

				clearCurrentTenantUnlessBelongsToOrg: (orgId: string) => {
					const { user, currentTenant } = get()
					if (!currentTenant || !orgId) {
						return
					}
					// No user yet means membership is unknown, not false — and currentTenant is persisted,
					// so clearing here loses the selection for good.
					if (!user) {
						return
					}
					if (!isPlatformUser(user)) {
						set({ currentTenant: '' })
						return
					}
					if (user.tenant_list?.length) {
						const belongs = user.tenant_list.some(t => t.id === currentTenant && t.org_id === orgId)
						if (!belongs) {
							set({ currentTenant: '' })
						}
						return
					}
					if (!user.tenants?.includes(currentTenant)) {
						set({ currentTenant: '' })
					}
				},

				clearScopeForDeletedOrg: (orgId: string) => {
					const { user, currentTenant, currentOrganizationId } = get()
					if (!orgId) {
						return
					}
					const next: Partial<AuthState> = {}
					if (currentOrganizationId === orgId) {
						next.currentOrganizationId = null
					}
					if (currentTenant) {
						if (!user || !isPlatformUser(user)) {
							next.currentTenant = ''
						} else if (user.tenant_list?.length) {
							if (user.tenant_list.some(t => t.id === currentTenant && t.org_id === orgId)) {
								next.currentTenant = ''
							}
						} else {
							// No tenant→org mapping available; clear so /me is not scoped to a deleted tenant
							next.currentTenant = ''
						}
					}
					if (Object.keys(next).length > 0) {
						set(next)
					}
				},

				setCurrentOrganizationId: (organizationId: string | null) => {
					set({ currentOrganizationId: organizationId })
				},

				// Recent tenants management
				addRecentTenant: (tenantId: string) => {
					if (!tenantId || typeof tenantId !== 'string') {
						console.warn('Invalid project ID provided to addRecentTenant:', tenantId)
						return
					}

					const { recentTenants } = get()

					// Remove tenant if it already exists (to avoid duplicates)
					const filtered = recentTenants.filter(id => id !== tenantId)

					// Add to front of array
					const updated = [tenantId, ...filtered]

					// Limit to maximum number of recent tenants
					const limited = updated.slice(0, MAX_RECENT_TENANTS)

					set({ recentTenants: limited })
				},

				// Tenant switching
				switchTenant: (tenantId: string, navigate?: (options: { to: string; replace?: boolean }) => void) => {
					const { user } = get()
					const pathParts = window.location.pathname.split('/').filter(Boolean)
					const projectsIndex = pathParts.indexOf('projects')
					const currentPathTenant = projectsIndex !== -1 ? pathParts[projectsIndex + 1] : undefined
					// Only skip when we're already ON a project route for this tenant. At org level (currentPathTenant undefined), navigate.
					if (currentPathTenant === tenantId) {
						return
					}

					// Only platform users can switch tenants
					if (!isPlatformUser(user)) {
						return
					}

					// Validate tenant access - users need direct access or owner privileges
					const canAccess = user.tenants?.includes(tenantId) || isOwner()
					if (!canAccess) {
						console.error(`Cannot switch to project "${tenantId}": insufficient permissions`)
						return
					}

					try {
						// Get current path to maintain the same page after tenant switch
						const currentPath = window.location.pathname
						const pathParts = currentPath.split('/').filter(Boolean)

						// Extract orgId from current path or use currentOrganizationId
						const orgsIndex = pathParts.indexOf('orgs')
						const orgId =
							orgsIndex !== -1 && pathParts[orgsIndex + 1] ? pathParts[orgsIndex + 1] : get().currentOrganizationId

						if (!orgId) {
							console.error('Cannot switch project: orgId not available')
							return
						}

						// Build new path: start with base project path
						let newPath = `/orgs/${orgId}/projects/${tenantId}`

						// Preserve the page path after tenant if we're already in a project route
						const projectsIndex = pathParts.indexOf('projects')
						if (projectsIndex !== -1) {
							const tenantIndex = projectsIndex + 1
							// Append any path segments after the current tenant ID
							if (pathParts.length > tenantIndex + 1) {
								let pagePath = pathParts.slice(tenantIndex + 1).join('/')
								// If viewing a specific dashboard, reset to dashboards list because
								// dashboard IDs are tenant-specific and will result in 404 in the new tenant.
								if (pagePath.startsWith('data/dashboards/view/')) {
									pagePath = 'data/dashboards/all'
								}
								newPath += `/${pagePath}`
							}
						}

						// Update tenant state first to ensure any refetches use the new tenant
						get().addRecentTenant(tenantId)
						set({ currentTenant: tenantId })

						// Clear query cache to fetch fresh data for the new tenant
						queryClient.clear()

						// Clear non-auth localStorage keys on tenant switch.
						// Custom view keys are preserved since they are tenant-scoped (keyed by tenant ID)
						// and the hook reads only the key for the active tenant.
						const keysToRemove = Object.keys(localStorage).filter(
							key => key !== AUTH_STORAGE_KEY && !key.startsWith(CUSTOM_VIEWS_STORAGE_KEY_PREFIX),
						)
						for (const key of keysToRemove) {
							localStorage.removeItem(key)
						}

						if (navigate) {
							navigate({ to: newPath, replace: true })
						} else {
							import('@/app/routes/routes').then(({ router }) => {
								router.navigate({ to: newPath })
							})
						}
					} catch (error) {
						console.error('Failed to switch project:', error)
					}
				},

				// State management actions
				setLoading: (loading: boolean) => {
					set({ isLoading: loading })
				},

				setError: (error: string | null) => {
					set({ error })
				},

				clearError: () => {
					set({ error: null })
				},

				clearAuthState: () => {
					// Clear auth state when 401 occurs (called by interceptors)
					// Router will handle redirect based on auth state change
					// The router's beforeLoad will check auth state on next navigation
					set({
						user: null,
						userType: null,
						currentTenant: '',
						recentTenants: [],
						isAuthenticated: false,
						isLoading: false,
						error: null,
						currentOrganizationId: null,
					})
				},

				setLastUsedProvider: (provider: string) => {
					set({ lastUsedProvider: provider })
				},

				// Utility actions
				hasPermission: (permission: keyof Permissions): boolean => {
					const { user } = get()
					if (!isPlatformUser(user) || !user.role?.permissions) {
						return false
					}

					return isPermissionGrantActive(user.role.permissions[permission])
				},

				hasRole: (roleName: string): boolean => {
					const { user } = get()
					if (!isPlatformUser(user)) {
						return false
					}
					return user.role?.name === roleName
				},

				getTenantRole: (tenantId: string): string | null => {
					const { user } = get()
					if (!isPlatformUser(user)) {
						return null
					}

					if (!user['tenant-roles']) {
						return user.role?.name || null
					}

					const tenantRole = user['tenant-roles'].find(tr => tr.tenant_id === tenantId)
					return tenantRole?.name || user.role?.name || null
				},

				canAccessTenant: (tenantId: string): boolean => {
					const { user } = get()
					if (isPlatformUser(user)) {
						return user.tenants?.includes(tenantId) || isOwner()
					}
					return false
				},
			})),
			{
				name: 'auth-storage',
				partialize: state => ({
					user: state.user,
					userType: state.userType,
					currentTenant: state.currentTenant,
					recentTenants: state.recentTenants,
					lastUsedProvider: state.lastUsedProvider,
					currentOrganizationId: state.currentOrganizationId,
				}),
				// Only persist non-sensitive data
				onRehydrateStorage: () => (state: AuthStore | undefined) => {
					if (state) {
						// Recalculate isAuthenticated based on persisted user
						state.isAuthenticated = !!state.user
						if (state.user && !state.userType) {
							state.userType = isConsoleUser(state.user) ? 'console' : 'platform'
						}
						if (state.user && !state.currentTenant) {
							state.currentTenant = isPlatformUser(state.user) ? state.user.tenant_id || '' : ''
						}
					}
				},
			},
		),
		{
			name: 'auth-store',
		},
	),
)

// Selectors for better performance
export const useUser = () => useAuthStore(state => state.user)
export const useCurrentTenant = () => useAuthStore(state => state.currentTenant)
export const useRecentTenants = () => useAuthStore(state => state.recentTenants)
export const useSwitchTenant = () => useAuthStore(state => state.switchTenant)
export const useAddRecentTenant = () => useAuthStore(state => state.addRecentTenant)
export const useIsAuthenticated = () => useAuthStore(state => state.isAuthenticated)
export const useIsLoading = () => useAuthStore(state => state.isLoading)
export const useAuthError = () => useAuthStore(state => state.error)
export const useGetTenantRole = () => useAuthStore(state => state.getTenantRole)

// Permission selectors
export const useHasPermission = (permission: keyof Permissions) =>
	useAuthStore(state => state.hasPermission(permission))

export const useHasRole = (roleName: string) => useAuthStore(state => state.hasRole(roleName))

export const useCanAccessTenant = (tenantId: string) => useAuthStore(state => state.canAccessTenant(tenantId))

// Billing/subscription selectors
export const useCurrentOrganizationIdFromState = () => useAuthStore(state => state.currentOrganizationId)

// Serial key label selector - returns frontend_settings.serial_key or fallback to 'Device ID'
export const useSerialKeyLabel = () =>
	useAuthStore(state => {
		if (!isPlatformUser(state.user)) {
			return 'Device ID'
		}
		return state.user['tenant-settings']?.frontend_settings?.serial_key || 'Device ID'
	})

// Action selectors
// Use useShallow to prevent infinite loops by doing shallow comparison of the returned object
export const useAuthActions = () =>
	useAuthStore(
		useShallow(state => ({
			login: state.login,
			logout: state.logout,
			refreshUser: state.refreshUser,
			setUser: state.setUser,
			setCurrentTenant: state.setCurrentTenant,
			setCurrentOrganizationId: state.setCurrentOrganizationId,
			setLoading: state.setLoading,
			setError: state.setError,
			clearError: state.clearError,
			setLastUsedProvider: state.setLastUsedProvider,
			clearAuthState: state.clearAuthState,
		})),
	)

/*
 * Subscription: Refresh user data (including tenant-settings) on tenant switch.
 * Safe from infinite loops as refreshUser() only writes currentTenant if empty.
 */
let prevTenant: string = useAuthStore.getState().currentTenant
useAuthStore.subscribe(
	state => ({
		currentTenant: state.currentTenant,
		isAuthenticated: state.isAuthenticated,
		user: state.user,
		refreshUser: state.refreshUser,
	}),
	({ currentTenant, isAuthenticated, user, refreshUser }) => {
		// Only refresh on actual tenant-to-tenant switches:
		// - Skip if not authenticated (logout clears tenant)
		// - Skip if tenant is empty (clearing state)
		// - Skip if previous tenant was empty (initial load / login — React Query handles that)
		// - Skip if tenant didn't actually change
		if (!isAuthenticated || !currentTenant || !prevTenant || currentTenant === prevTenant) {
			prevTenant = currentTenant
			return
		}

		// Skip if user is not a member of the tenant (e.g. Org Owner accessing via overriding permissions)
		// Calling /api/v3/me in this context checks for explicit tenant membership and would fail with 403
		if (isPlatformUser(user) && !user.tenants?.includes(currentTenant)) {
			prevTenant = currentTenant
			return
		}

		prevTenant = currentTenant

		// Fetch fresh user data (including tenant-settings) for the new tenant
		refreshUser().catch(err => {
			console.error('Failed to refresh user after tenant switch:', err)
		})

		// Refresh quota usage for the new tenant so access-control hooks have fresh data.
		// Dynamic import avoids circular dependency between authStore and quotaStore.
		import('./quotaStore')
			.then(({ useQuotaStore }) => useQuotaStore.getState().refreshQuotaUsage(currentTenant))
			.catch(err => {
				console.error('Failed to refresh quota usage after tenant switch:', err)
			})
	},
	{ equalityFn: (a, b) => a.currentTenant === b.currentTenant && a.isAuthenticated === b.isAuthenticated },
)
